import { Truck } from "lucide-react";
import { cn } from "@/lib/utils";
import type { Quote } from "../api/types";
import { PageState, SurfaceCard } from "../ui/page";

// Carrier quotes take seconds. While they price, each manufacturer's shipment gets a lane a truck
// crosses, and its price waits as a placeholder. Keyframes live in src/index.css.

type PendingShipment = { key: string; manufacturer: string; items: number };

/** The shipments Shipping will price: one per manufacturer on the quote, by name. */
function pendingShipments(quote: Quote): PendingShipment[] {
  const groups = new Map<number, PendingShipment>();
  for (const line of quote.lines) {
    if (line.parent_line_id || line.manufacturer_id == null) continue;
    const group = groups.get(line.manufacturer_id) ?? {
      key: String(line.manufacturer_id),
      manufacturer: line.manufacturer || "Manufacturer",
      items: 0,
    };
    group.items += 1;
    groups.set(line.manufacturer_id, group);
  }
  return [...groups.values()].sort((a, b) =>
    a.manufacturer.localeCompare(b.manufacturer),
  );
}

/** The Shipping tab's first load: what is being priced, and that it is moving. */
export function PricingFreight({ quote }: { quote: Quote }) {
  const shipments = pendingShipments(quote);
  if (!shipments.length) {
    return (
      <SurfaceCard title="Shipping">
        <PageState compact kind="loading" title="Pricing freight" />
      </SurfaceCard>
    );
  }
  return (
    <SurfaceCard
      actions={
        <span className="text-xs text-muted-foreground" role="status">
          Pricing freight
        </span>
      }
      aria-busy="true"
      title="Shipping"
    >
      <ul className="divide-y">
        {shipments.map((shipment, index) => (
          <li
            className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-x-4 gap-y-2 px-4 py-3 sm:grid-cols-[minmax(8rem,14rem)_minmax(0,1fr)_auto]"
            key={shipment.key}
          >
            <span className="min-w-0 truncate text-sm font-semibold">
              {shipment.manufacturer}
              <span className="ml-2 text-xs font-normal text-muted-foreground">
                {shipment.items} {shipment.items === 1 ? "item" : "items"}
              </span>
            </span>
            <span
              aria-hidden="true"
              className="relative col-start-2 row-start-1 h-4 w-16 overflow-hidden rounded bg-muted sm:col-start-3"
            >
              <span
                className="absolute inset-y-0 left-0 w-1/2 animate-[qb-freight-shimmer_1.6s_ease-in-out_infinite] bg-gradient-to-r from-transparent via-background/80 to-transparent motion-reduce:hidden"
                style={{ animationDelay: `${index * 0.2}s` }}
              />
            </span>
            <FreightLane delay={index * 0.35} />
          </li>
        ))}
      </ul>
    </SurfaceCard>
  );
}

/** A dashed lane with a truck driving its length, leaving a solid trail. */
function FreightLane({ delay }: { delay: number }) {
  return (
    <span
      aria-hidden="true"
      className="relative col-span-2 h-4 overflow-hidden sm:col-span-1 sm:col-start-2 sm:row-start-1"
    >
      <span className="absolute inset-x-0 top-1/2 border-t border-dashed border-muted-foreground/30" />
      <span
        className="absolute inset-0 -translate-x-1/2 animate-[qb-freight-lane_2.8s_cubic-bezier(0.65,0,0.35,1)_infinite] motion-reduce:animate-none"
        style={{ animationDelay: `${delay}s` }}
      >
        <span className="absolute top-1/2 right-5 left-0 border-t border-foreground/35" />
        <Truck className="absolute top-0 right-0 size-4 text-foreground/70" />
      </span>
    </span>
  );
}

/** A thin line across a card while Shipping reprices behind prices already on screen. */
export function RepricingLine({ active }: { active: boolean }) {
  return (
    <div
      aria-hidden="true"
      className={cn(
        "relative h-0.5 overflow-hidden transition-opacity duration-300",
        active ? "opacity-100" : "opacity-0",
      )}
    >
      {active ? (
        <span className="absolute inset-y-0 left-0 w-1/3 animate-[qb-freight-shimmer_1.2s_cubic-bezier(0.65,0,0.35,1)_infinite] bg-primary/60 motion-reduce:w-full motion-reduce:animate-none motion-reduce:bg-primary/25" />
      ) : null}
    </div>
  );
}
